import React from "react";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCbomContext } from "../context/CbomContext";
import UploadSensorAgent from "./modals/UploadSensorAgent";
import { fetchSensorAgent, uploadSensorAgent } from "../services/apiServices";
import { sortData } from "../utils/sortData";
import ViewSensorAgent from "./modals/ViewSensorAgent";
import { useProjectContext } from "context/ProjectContext";

export interface SensorAgent {
  sensor_agent_id: number;
  sensor_agent_name: string;
  sensor_agent_type: string;
  sensor_agent_os_type: string;
  sensor_agent_cpu_type: string;
  sensor_agent_state?: string;
  device_usage_count: number;
  sensor_agent_url?: string;
  sensor_agent_version?: string;
  created_at?: string;
}

export const fieldLabels: Partial<Record<keyof SensorAgent, string>> = {
  sensor_agent_id: "Agent Sensor ID",
  sensor_agent_name: "Name",
  sensor_agent_type: "Type",
  sensor_agent_os_type: "OS Type",
  sensor_agent_cpu_type: "CPU Type",
  device_usage_count: "No of Devices Using It",
  sensor_agent_state: "State",
};
const SensorAgent: React.FC = () => {
  const {
    setSelectedAgent,
    showAgentViewForm,
    setShowAgentViewForm,
    setSelectedAgentData,
  } = useCbomContext();
  const { state } = useProjectContext();
  const [agentData, setAgentData] = useState<SensorAgent[]>([]);
  const sensorAgentFields = Object.keys(fieldLabels) as (keyof SensorAgent)[];
  const { showAgentUploadForm, setShowAgentUploadForm } = useCbomContext();
  const [currentItems, setCurrentItems] = useState<SensorAgent[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredSources, setFilteredSources] = useState<SensorAgent[]>([]);
  const [sortField, setSortField] =
    useState<keyof SensorAgent>("sensor_agent_id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil((filteredSources?.length || 0) / itemsPerPage);
  const navigate = useNavigate();
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };
  useEffect(() => {
    if (state.currentProjectID) {
      fetchSensorAgent(setAgentData, state.currentProjectID);
    }
  }, [
    localStorage.getItem("authToken"),
    state.currentProjectID,
    showAgentUploadForm,
  ]);
  useEffect(() => {
    if (searchTerm) {
      const filtered = agentData.filter(
        (e) =>
          e &&
          Object.values(e)
            .filter(
              (value) => typeof value === "string" || typeof value === "number"
            )
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredSources(filtered);
    } else {
      setFilteredSources(agentData); // Show all data if search term is cleared
      // console.log(agentData);
    }
  }, [searchTerm, agentData]);
  useEffect(() => {
    if (sortField) {
      const sortedData = sortData(filteredSources, sortField, sortOrder);
      setFilteredSources(sortedData);
    }
  }, [sortField, sortOrder]);
  useEffect(() => {
    setCurrentItems(
      Array.isArray(filteredSources)
        ? filteredSources.slice(indexOfFirstItem, indexOfLastItem)
        : []
    );
  }, [filteredSources, currentPage, itemsPerPage]);

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };
  const handleSort = (field: keyof SensorAgent) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  const handleAgentClick = (id: number) => {
    const selectedRow = agentData.find((agent) => agent.sensor_agent_id === id);
    if (selectedRow) {
      setSelectedAgent(id);
      setSelectedAgentData(selectedRow);
      setShowAgentViewForm(true);
    }
  };

  return (
    <>
      <div className="h-full p-2 bg-white rounded-lg flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-custom-blue7 pl-2">
            Sensor Agents
          </h1>
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue5 bg-custom-blue8"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
          <button
            className="p-2 bg-custom-blue7 rounded-full"
            onClick={() => {
              setShowAgentUploadForm(true);
            }}
          >
            <Plus size={20} className="text-gray-50" />
          </button>
        </div>
        {showAgentUploadForm && <UploadSensorAgent />}
        <div className="flex flex-col bg-custom-blue8 border rounded-md p-2 flex-grow min-h-0">
          <div className="overflow-auto flex-grow custom-scrollbar">
            <table
              id="data-table"
              className="min-w-full bg-white shadow border rounded-md"
            >
              <thead className="sticky top-0 z-10 bg-custom-blue0">
                <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                  {sensorAgentFields.map((field) => (
                    <th
                      key={field}
                      className="p-2"
                      onClick={() => handleSort(field)}
                    >
                      {fieldLabels[field]}
                      {sortField === field && (
                        <span className="ml-1">
                          {sortOrder === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems &&
                Array.isArray(currentItems) &&
                currentItems.length > 0 ? (
                  currentItems.map((source, index) => {
                    return (
                      <tr
                        key={source.sensor_agent_id}
                        className={`${
                          index % 2 === 1 ? "bg-custom-blue0" : ""
                        } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                      >
                        {sensorAgentFields.map((field) => (
                          <td key={field} className="p-2">
                            {field === "sensor_agent_id" ? (
                              <button
                                onClick={() =>
                                  handleAgentClick(source.sensor_agent_id)
                                }
                                className="text-blue-600 underline hover:text-blue-800"
                              >
                                {source[field] ? String(source[field]) : "N/A"}
                              </button>
                            ) : (
                              source[field] ?? "N/A"
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={15}
                      className="text-custom-blue7 p-2 text-center"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {showAgentViewForm && <ViewSensorAgent />}
          <div className="flex justify-between items-center mt-2">
            <div className="flex items-center space-x-2">
              <select
                className="border rounded-md p-2 bg-white"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                {/* <option value={5}>5</option> */}
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
                <option value={50}>50</option>
              </select>
              <span className="text-custom-blue7">Results per page</span>
            </div>
            <div className="flex items-center space-x-2 text-custom-blue7">
              <span>
                Displaying {indexOfFirstItem + 1}-
                {Math.min(indexOfLastItem, filteredSources?.length)} of{" "}
                {filteredSources?.length}
              </span>
              <button
                className="p-2 bg-white rounded-md"
                onClick={prevPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                className="p-2 bg-white rounded-md"
                onClick={nextPage}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SensorAgent;
